import { useState, useEffect, useRef, FC } from 'react';

import CountrySelect from '../../../shared/components/CountrySelect';
import { 
    BILLING_ADDRESS_TITLE, 
    REGION_RESTRICTION_MESSAGE, 
    ERRORS, 
    COUNTRY_CODES,
    DEFAULT_COUNTRY_NAME,
    ADDRESS_FIELDS
} from '../constants/billingDetails.constants';

import ManualAddressForm from './ManualAddressForm';
// import AddressCapture from '../../../features/loqate/AddressCapture';

export interface InitialAddress {
    country?: string;
    countryName?: string;
    line1?: string;
    streetName?: string;
    line2?: string;
    houseNumber?: string;
    city?: string;
    state?: string;
    province?: string;
    postcode?: string;
    postalCode?: string;
    zip?: string;
}

export interface AddressState extends InitialAddress {
    [key: string]: string | undefined;
}

interface BillingAddressFormProps {
    onValidityChange?: (isValid: boolean) => void;
    onAddressChange?: (address: AddressState) => void;
    errors?: Record<string, string>;
    onClearErrors?: () => void;
    initialAddress?: InitialAddress;
    onInteraction?: () => void;
}

interface CountryState {
    code: string;
    name: string;
}

const BillingAddressForm: FC<BillingAddressFormProps> = ({ onValidityChange, onAddressChange, errors = {}, onClearErrors, initialAddress, onInteraction }) => {
    const [country, setCountry] = useState<CountryState>(
        initialAddress?.country
            ? { code: initialAddress.country, name: initialAddress.countryName || initialAddress.country }
            : { code: COUNTRY_CODES.US, name: DEFAULT_COUNTRY_NAME }
    );

    // Normalize initial address: mapped backend keys (streetName) to form keys (line1)
    const normalizedInitialAddress: AddressState = initialAddress ? {
        ...initialAddress,
        [ADDRESS_FIELDS.LINE1]: initialAddress.line1 || initialAddress.streetName || '',
        [ADDRESS_FIELDS.LINE2]: initialAddress.line2 || initialAddress.houseNumber || '',
        [ADDRESS_FIELDS.POSTCODE]: initialAddress.postcode || initialAddress.postalCode || initialAddress.zip || '',
        [ADDRESS_FIELDS.ZIP]: initialAddress.zip || initialAddress.postalCode || initialAddress.postcode || '', // Support both for safety
        [ADDRESS_FIELDS.STATE]: initialAddress.state || initialAddress.province || ''
    } : {};

    // const [mode, setMode] = useState(initialAddress && Object.keys(initialAddress).length > 0 ? 'manual' : 'search');
    const [address, setAddress] = useState<AddressState>(normalizedInitialAddress);
    // const [searchAddress, setSearchAddress] = useState('');

    // const [dynamicRules, setDynamicRules] = useState({ showState: false, showBuilding: false });

    const onClearErrorsRef = useRef(onClearErrors);
    useEffect(() => { onClearErrorsRef.current = onClearErrors; }, [onClearErrors]);

    const prevCountryRef = useRef(country.code);

    useEffect(() => {
        if (prevCountryRef.current !== country.code) {
            setAddress(prev => ({ ...prev, [ADDRESS_FIELDS.STATE]: '', [ADDRESS_FIELDS.PROVINCE]: '' }));
            if (onClearErrorsRef.current) onClearErrorsRef.current();
            prevCountryRef.current = country.code;
        }
    }, [country.code]);

    useEffect(() => {
        const checkValidity = (): boolean => {
            if (country.code === COUNTRY_CODES.RU) return false;

            // if (mode === 'search') {
            //     return searchAddress.trim().length > 0;
            // } else {
            const hasLine1 = (address[ADDRESS_FIELDS.LINE1] ?? '').trim().length > 0;
            const hasCity = (address[ADDRESS_FIELDS.CITY] ?? '').trim().length > 0;
            const hasPostalCode = ((address[ADDRESS_FIELDS.POSTCODE] ?? '').trim().length > 0) ||
                ((address[ADDRESS_FIELDS.ZIP] ?? '').trim().length > 0);

            let hasState = true;
            if (country.code === COUNTRY_CODES.US || country.code === COUNTRY_CODES.CA) {
                hasState = !!address[ADDRESS_FIELDS.STATE] && (address[ADDRESS_FIELDS.STATE] ?? '').trim().length > 0;
            }

            return !!(hasLine1 && hasCity && hasPostalCode && hasState);
            // }
        };

        if (onValidityChange) {
            onValidityChange(checkValidity());
        }

        if (onAddressChange) {
            onAddressChange({
                ...address,
                country: country.code,
                countryName: country.name
            });
        }
    }, [country /*, mode */, address /*, searchAddress */, onValidityChange, onAddressChange]); //enable this line once loqate is back

    const updateField = (key: string, value: string) => {
        setAddress(prev => ({ ...prev, [key]: value }));
        if (onInteraction) onInteraction();
    };

    /*
    const handleAddressSelected = (selectedAddress) => {
        // 
        if (onClearErrors) {
            onClearErrors();
        }
        const newAddress = {
            line1: selectedAddress.addressLine1,
            line2: selectedAddress.addressLine2,
            city: selectedAddress.city,
            state: selectedAddress.region,
            postcode: selectedAddress.postalCode,
            zip: selectedAddress.postalCode
        };

        setAddress(prev => ({ ...prev, ...newAddress }));
        setSearchAddress(selectedAddress.addressLine1);
        setMode('manual');
        if (onInteraction) onInteraction();
    };

    const handleManualClick = () => {
        setMode('manual');
        if (onInteraction) onInteraction();
    };

    const handleSearchClick = () => {
        setMode('search');
        if (onInteraction) onInteraction();
    };
    */

    const localErrors: Record<string, string> = { ...errors };
    const hasPostalCodeStarted: boolean = !!(((address[ADDRESS_FIELDS.ZIP] ?? '').length > 0) || ((address[ADDRESS_FIELDS.POSTCODE] ?? '').length > 0));
    const isStateEmpty = !address[ADDRESS_FIELDS.STATE] || (address[ADDRESS_FIELDS.STATE] ?? '').trim().length === 0;

    if (hasPostalCodeStarted && isStateEmpty) {
        if (country.code === COUNTRY_CODES.US) {
            localErrors[ADDRESS_FIELDS.STATE] = ERRORS.STATE_REQUIRED;
        } else if (country.code === COUNTRY_CODES.CA) {
            localErrors[ADDRESS_FIELDS.STATE] = ERRORS.PROVINCE_REQUIRED;
        }
    }

    return (
        <div className="w-full">
            <h2 className="font-noto font-semibold text-[18px] text-[#002B66] mb-[12px] w-[295px] md:w-[600px] xl:w-[573px] h-[26px] leading-[26px]">{BILLING_ADDRESS_TITLE}</h2>

            <div className="w-[295px] md:w-[640px] xl:w-[573px]">
                <div className="md:w-[600px] xl:w-full">
                    <CountrySelect value={country.code} onChange={(v: CountryState) => { setCountry(v); if (onInteraction) onInteraction(); }} />
                </div>

                <div className="w-full md:w-[600px] xl:w-full h-[0px] border-t border-[#D0D5DD] my-[24px]"></div>


            {country.code === COUNTRY_CODES.RU ? (
                <div className="mt-4 w-full h-[80px] bg-[#F9FAFB] rounded-[8px] py-[16px] px-[24px] flex items-center justify-center gap-[24px]">
                    <p className="font-noto font-normal text-[12px] leading-[20px] text-[#667085] m-0 text-center">
                        {REGION_RESTRICTION_MESSAGE}
                    </p>
                </div>
            ) : (
                <>
                    {/* mode === 'search' ? (
                        <>
                            <AddressCapture
                                country={country.code}
                                onAddressSelected={handleAddressSelected}
                                onInteraction={onInteraction}
                            />
                            <div className="text-center mt-3 mb-0">
                                <button
                                    type="button"
                                    onClick={handleManualClick}
                                    className="bg-transparent border-none font-noto font-normal text-[14px] text-[#667085] leading-[20px] cursor-pointer underline hover:text-[#182230] p-0"
                                >
                                    Enter address manually
                                </button>
                            </div>
                        </>
                    ) : */ (
                            <>
                                <ManualAddressForm
                                    country={country.code}
                                    address={address as unknown as Record<string, string>}
                                    onChange={updateField}
                                    errors={localErrors}
                                    // dynamicRules={dynamicRules}
                                    onInteraction={onInteraction}
                                />
                                {/* <div className="text-center mt-0 mb-0">
                                <button
                                    type="button"
                                    onClick={handleSearchClick}
                                    className="bg-transparent border-none font-noto font-normal text-[14px] text-[#667085] leading-[20px] cursor-pointer underline hover:text-[#182230] p-0"
                                >
                                    Enter address automatically
                                </button>
                            </div> */}
                            </>
                        )}
                </>
            )}
            </div>
        </div>
    );
};

export default BillingAddressForm;
